import React, { useState, useEffect, useMemo } from 'react'
import { XYChart, AnimatedAxis, DataProvider, AnimatedAreaSeries, Tooltip } from '@visx/xychart'
import { ParentSize } from '@visx/responsive'
import { Flex, PropertyGrid, Text, DateTimeRangePicker } from '@leverege/ui-elements'
import { curveStepAfter } from '@visx/curve'
import { Attributes } from '@leverege/ui-attributes'
import { DateTime } from 'luxon'
import { LoadingView } from '@leverege/molten/views'
import classnames from 'classnames'
import useOccupancyGraphData from './useOccupancyGraphData'
import ChartLegend from './ChartLegend'
import { occupancyTheme } from './GraphTheme'
import S from './OccupancyGraph.css'
import GetStageFormat from '../../../../util/Stage'
import { categories } from '../../../../Constants'

const GRAPH_HEIGHT = 300
const GRAPH_MARGIN = {
  left : 65,
  right : 30,
  top : 30,
  bottom : 50
}
const LEGEND_WIDTH = 230

function RenderTooltip(
  { tooltipData, colorScale },
  { context : { clientProps : { delegate } }, data : lines, dispatch }
) {
  const [ imgURL, setImgURL ] = useState()
  const [ isLoadingImg, setIsLoadingImg ] = useState( false )  
  const key = tooltipData?.nearestDatum?.key
  
  const halves = tooltipData?.nearestDatum?.datum?.halves ?? { none : {} }
  
  const a = Object.entries( halves )
  const [ deviceId, moldHalfObj ] = a[0]
  
  const hour = tooltipData?.nearestDatum?.datum?.hour ?? 0
  const parentLineId = moldHalfObj?.parentLineId
  const workerCount = moldHalfObj?.count
  const imgSrc = moldHalfObj?.imgSrc ?? ''

  const {
    [categories.DIRECT] : direct,
    [categories.INDIRECT] : indirect,
    [categories.INDIRECT_OTHER] : indirectOther,
    [categories.UNCATEGORIZED] : uncategorized,
  } = moldHalfObj?.countByCategory || {}

  const hourMS = new Date( hour ).getTime()

  useEffect( () => {
    let cancelled = false
    setIsLoadingImg( true )
    setImgURL( false )
    const line = lines?.items?.find( l => l.id === parentLineId )
    const lwuw = line?.data?.data?.uwMold.id === deviceId ? 'uwMold' : 'lwMold'
    // debounce image loading to prevent too many requests
    const timer = setTimeout(
      () => {
        if ( !cancelled ) {
          dispatch( delegate.item( parentLineId ).child( lwuw ).resource( imgSrc ).get() ).then( ( ( result ) => {
            if ( !cancelled ) {
              setImgURL( result.data.url )
            }
          } ) ).finally( () => {
            // only success should set loading to false
            if ( !cancelled ) {
              setIsLoadingImg( false )
            }
          } )
        }
      },
      100
    )
    return () => {
      cancelled = true
      clearTimeout( timer )
    }
  }, [ imgSrc, delegate, deviceId, dispatch, parentLineId, lines?.items ] )
  
  const moldHalf = key
  const stageName = moldHalfObj?.stage ?? 'n/a'
  
  return (
    <PropertyGrid
      labelVariant="chartRolloverLabel|cycleDetailLabel|detailLabel"
      headerVariant="chartRolloverHeader"
      variant="occupancyGraphDetailCard|detailCard"
      className={S.propertyGrid}>
      <PropertyGrid.Header title="Shell Details" />
      { ( imgSrc && isLoadingImg ) || imgURL ? (
        <PropertyGrid.Item fill>
          <RsrcImage src={imgURL} loading={isLoadingImg} />
        </PropertyGrid.Item>
      ) : null}
      <PropertyGrid.Item
        label="Worker Count">
        <Text variant="ogDetailValue|detailValue">
          {workerCount ?? '-'}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item
        label="Direct">
        <Text variant="ogDetailValue|detailValue">
          {direct ?? '-'}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item
        label="Indirect">
        <Text variant="ogDetailValue|detailValue">
          {indirect ?? '-'}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item
        label="Indirect Other">
        <Text variant="ogDetailValue|detailValue">
          {indirectOther ?? '-'}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item
        label="Uncategorized">
        <Text variant="ogDetailValue|detailValue">
          {uncategorized ?? '-'}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item
        label="Stage Name">
        <Text variant="ogDetailValue|detailValue">
          {GetStageFormat( stageName ).name}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item stretch label="Shell">
        <Text variant="ogDetailValue|detailValue">
          {moldHalf}
        </Text>
      </PropertyGrid.Item>
      <PropertyGrid.Item stretch label="Time">
        <Text variant="ogDetailValue|detailValue">
          {DateTime.fromMillis( hourMS ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}
        </Text>
      </PropertyGrid.Item>
    </PropertyGrid>
  ) 
}

export default function OccupancyGraph( props ) {
  
  const {
    data : lines,
    lwButtonActive,
    allLinesActive,
    context : { loading }
  } = props
  const range = props?.context?.clientProps?.settings?.options?.range
  const timeRange = useMemo(
    () => {
      return DateTimeRangePicker.getRange( range )
    },
    [ range ]
  )

  const sidesToShow = useMemo(
    () => {
      return allLinesActive ? [ 'uwMold', 'lwMold' ] : lwButtonActive ? [ 'lwMold' ] : [ 'uwMold' ]
    },
    [ allLinesActive, lwButtonActive ]
  )
  
  const { data : cData, config } = useOccupancyGraphData( { ...props, sidesToShow, timeRange } )
  const [ lastData, setLastData ] = useState( cData )
  useEffect(
    () => {
      if ( cData.length > 0 && !loading ) {
        setLastData( cData )
      }
    },
    [ cData, loading ]
  )
  const data = lastData

  if ( data.length === 0 ) {
    return <LoadingView title="Loading" />
  }
    
  const sortedData = data.sort( ( a, b ) => {
    const aMS = new Date( a.hour ).getTime()
    const bMS = new Date( b.hour ).getTime()
    return aMS - bMS
  } )

  const realColors = [], dashed = {}
  let colorIndex = 0, nextIsDashed = false
  const series = lines?.items?.reduce( ( prev, item, index ) => {
    const line = item
    const lineName = Attributes.get( 'name', line ) || line.data.name

    const uwMold = item?.data?.data?.uwMold
    const lwMold = item?.data?.data?.lwMold
    
    const uwId = uwMold?.id
    const lwId = lwMold?.id

    const uwName = `${lineName} - HP`
    const lwName = `${lineName} - LP`

    const showLWData = sidesToShow.includes( 'lwMold' )
    const showUWData = sidesToShow.includes( 'uwMold' )
    if ( showUWData && uwId ) {
      prev.push(
        <AnimatedAreaSeries
          lineProps={{ strokeDasharray : nextIsDashed ? '6 7' : null }}
          fillOpacity={0.5}
          renderLine
          key={uwId}
          dataKey={uwName}
          data={sortedData}
          curve={curveStepAfter}
          {...config.accessors.halves[uwId]} />
      )
      dashed[uwName] = nextIsDashed
      realColors.push( occupancyTheme.colors[colorIndex % occupancyTheme.colors.length] )
    }
    if ( uwId ) {
      nextIsDashed = !nextIsDashed
      colorIndex++
    }
    if ( showLWData && lwId ) {
      prev.push(
        <AnimatedAreaSeries
          lineProps={{ strokeDasharray : nextIsDashed ? '6 7' : null }}
          fillOpacity={0.5}
          renderLine
          key={lwId}
          dataKey={lwName}
          data={sortedData}
          curve={curveStepAfter}
          {...config.accessors.halves[lwId]} />
      )
      dashed[lwName] = nextIsDashed
      realColors.push( occupancyTheme.colors[colorIndex % occupancyTheme.colors.length] )
    }
    if ( lwId ) {
      nextIsDashed = !nextIsDashed
      colorIndex++
    }
    return prev
  }, [] )

  const themeToUse = {
    ...occupancyTheme,
    colors : realColors
  }
  
  return (
    <ParentSize>
      {( { width, height } ) => {
        return (
          <DataProvider
            theme={themeToUse}
            xScale={config.scale.x}
            yScale={config.scale.y}>
            <Flex direction="row">
              <div className={S.legend}>
                <ChartLegend dashed={dashed} />
              </div>
              <div>
                <XYChart
                  width={width - LEGEND_WIDTH}
                  height={GRAPH_HEIGHT}
                  margin={GRAPH_MARGIN}>
                  <Tooltip
                    snapTooltipToDatumX
                    snapTooltipToDatumY
                    showVerticalCrosshair
                    showSeriesGlyphs
                    className={S['custom-tooltip']}
                    anchor="bottom" // Options: top, left, bottom, right, etc.
                    renderTooltip={o => RenderTooltip( o, props )}/>
                  <AnimatedAxis
                    orientation="left"
                    numTicks={10}
                    tickFormat={config.tickFormat.y}
                    label="Total Count"/>
                  <AnimatedAxis
                    label="Time"
                    tickFormat={config.tickFormat.x}
                    orientation="bottom"
                    numTicks={10}/>
                  {series}
                </XYChart>
              </div>
            </Flex>
          </DataProvider>
        )
      }}
    </ParentSize>
  )
}

function RsrcImage( { src, loading } ) {
  let imageOrLoading
  if ( loading ) {
    imageOrLoading = (
      <Flex align="center" justify="center" className={S.iconWrapper}>
        <i className={classnames( S.loading, 'fa-solid fa-spin fa-spinner' )} />
      </Flex>
    )
  } else if ( !src ) {
    imageOrLoading = (
      <Flex className={S.iconWrapper}>
        <i className={classnames( S.loading, 'fa-regular fa-image' )} />
      </Flex>
    )
  } else {
    imageOrLoading = (
      <img className={S.workerCountImg} alt="workerCountImg" src={src}/>
    )
  }
  return (
    <div className={S.workerCountImgContainer} >
      {imageOrLoading}
    </div>
  )
}
